/**
 * Central configuration for every simulation language.
 *
 * Each entry drives:
 *  - The AI customer's roleplay instructions (conversational register + cultural/communication norms)
 *  - Feedback/assessment guidance (what "polite", "empathetic" and "clear" mean in that culture)
 *  - The language that ALL AI-generated output (transcripts, scripts, feedback, scoring rationale) must use
 *  - Culturally appropriate customer names
 *  - Localized labels for the feedback screen
 *
 * The `value` is what is stored in `SimulationSettings.language`, so existing values
 * ('English', 'Taglish', 'Visayan (Bisaya)') must never change.
 */

export interface FeedbackUiLabels {
    overall: string;
    simulationResults: string;
    assessmentFrameworkApplied: string;
    downloadReport: string;
    newTraining: string;
    scenario: string;
    channel: string;
    duration: string;
    customer: string;
    timelineAnalysis: string;
    focusedFor: (seconds: number) => string;
}

export interface LanguageConfig {
    /** Stored value in SimulationSettings.language */
    value: string;
    /** Label shown in the language dropdown */
    label: string;
    /** BCP-47 code (used for the voice session and the `lang` attribute) */
    bcp47: string;
    /** Text direction for rendering */
    dir: 'ltr' | 'rtl';
    /** Name of the language as the AI should write it in instructions, e.g. "Japanese (日本語)" */
    promptName: string;
    /** How the AI customer must speak/write, including cultural and communication norms */
    customerInstruction: string;
    /** How the evaluator should judge tone, empathy and clarity for this language/culture */
    assessmentGuidance: string;
    /** Email closing line used in the auto-generated signature */
    emailClosing: string;
    /** Optional culturally appropriate names. If omitted, the default (English) name pool is used. */
    names?: { male: string[]; female: string[]; last: string[]; familyNameFirst?: boolean; separator?: string };
    /** Optional localized feedback screen labels. If omitted, English labels are used. */
    ui?: FeedbackUiLabels;
}

export const ENGLISH_UI: FeedbackUiLabels = {
    overall: 'Overall',
    simulationResults: 'Simulation Results',
    assessmentFrameworkApplied: 'Assessment Framework Applied',
    downloadReport: 'Download Report',
    newTraining: 'New Training',
    scenario: 'Scenario',
    channel: 'Channel',
    duration: 'Duration',
    customer: 'Customer',
    timelineAnalysis: 'Timeline Analysis',
    focusedFor: (s) => `Focused for ~${s}s`,
};

export const LANGUAGES: LanguageConfig[] = [
    {
        value: 'English',
        label: 'English',
        bcp47: 'en-US',
        dir: 'ltr',
        promptName: 'English',
        customerInstruction: 'Strictly use English for all communications.',
        assessmentGuidance: 'Standard English norms apply. Evaluate tone, emotion, and clarity based on professional English communication standards.',
        emailClosing: 'Best regards,',
    },
    {
        value: 'Taglish',
        label: 'Taglish (Filipino/Tagalog)',
        bcp47: 'fil-PH',
        dir: 'ltr',
        promptName: 'Taglish',
        customerInstruction: 'You MUST converse in Taglish. Mix Tagalog and English naturally.',
        assessmentGuidance: 'Taglish (Tagalog-English mix) is expected. Do not penalize for language mixing or colloquialisms. Evaluate based on the core customer service standards applied to this specific bilingual context, paying specific attention to Taglish language conventions, emotional nuances, and cultural communication norms.',
        emailClosing: 'Best regards,',
    },
    {
        value: 'Visayan (Bisaya)',
        label: 'Visayan (Bisaya)',
        bcp47: 'ceb-PH',
        dir: 'ltr',
        promptName: 'Visayan (Bisaya)',
        customerInstruction: 'You MUST converse in Visayan (Bisaya). Use Bisaya naturally for all communications.',
        assessmentGuidance: 'Visayan (Bisaya) is expected. Evaluate performance based on clarity, empathy, and professional communication standards in Bisaya, paying specific attention to Visayan language conventions, emotional nuances, and cultural communication norms.',
        emailClosing: 'Best regards,',
    },
    {
        value: 'Japanese',
        label: 'Japanese (日本語)',
        bcp47: 'ja-JP',
        dir: 'ltr',
        promptName: 'Japanese (日本語)',
        customerInstruction: `You MUST converse ONLY in natural, conversational Japanese (日本語) as a real customer in Japan would. Follow Japanese cultural and communication norms:
- As the customer, speak in polite です/ます form by default. You may become slightly more direct if frustrated, but stay within what a Japanese customer would realistically say; avoid crude speech unless your persona explicitly demands it.
- Japanese communication is indirect and high-context: soften complaints and requests with cushion phrases (e.g., 「すみません、」「恐れ入りますが」「〜なんですけど…」「ちょっと困っていまして」).
- Expect the agent to use proper 敬語 (尊敬語/謙譲語) such as 「かしこまりました」「申し訳ございません」「少々お待ちくださいませ」. React realistically (subtle discomfort or dissatisfaction) if the agent is too casual or blunt.
- Refer to people with appropriate honorifics (〜さん/〜様), never refer to yourself with 様.
- Use natural Japanese aizuchi in voice/chat (「はい」「ええ」「なるほど」) where appropriate.
- In emails, follow Japanese business email etiquette: open with a polite opener (e.g., 「お世話になっております。」 or 「突然のご連絡失礼いたします。」), be structured and courteous.
- Write in standard Japanese script (kanji, hiragana, katakana). Do NOT use romaji.`,
        assessmentGuidance: `Japanese is expected. Evaluate against Japanese customer service (おもてなし) standards: correct and consistent use of 敬語 (尊敬語・謙譲語・丁寧語), cushion words (クッション言葉), proper apology expressions (「大変申し訳ございません」), acknowledging the customer's inconvenience before solving, indirect and humble phrasing, and polite closings (「お問い合わせいただきありがとうございました」). Overly casual or blunt phrasing should be treated as a significant tone issue. Recommended scripts must use natural, professional Japanese keigo.`,
        emailClosing: 'よろしくお願いいたします。',
        names: {
            male: ['大翔', '蓮', '健太', '翔太', '拓也', '大輔', '直樹', '浩二', '誠', '隆'],
            female: ['陽菜', '結衣', '美咲', 'さくら', '愛', '由美', '恵子', '真由美', '彩', '奈々'],
            last: ['佐藤', '鈴木', '高橋', '田中', '伊藤', '渡辺', '山本', '中村', '小林', '加藤', '吉田', '山田'],
            familyNameFirst: true,
            separator: ' ',
        },
        ui: {
            overall: '総合',
            simulationResults: 'シミュレーション結果',
            assessmentFrameworkApplied: '適用された評価フレームワーク',
            downloadReport: 'レポートをダウンロード',
            newTraining: '新しいトレーニング',
            scenario: 'シナリオ',
            channel: 'チャネル',
            duration: '所要時間',
            customer: 'お客様',
            timelineAnalysis: 'タイムライン分析',
            focusedFor: (s) => `約${s}秒`,
        },
    },
    {
        value: 'Spanish',
        label: 'Spanish (Español)',
        bcp47: 'es-US',
        dir: 'ltr',
        promptName: 'Spanish (Español)',
        customerInstruction: `You MUST converse ONLY in natural, conversational Spanish (neutral Latin American Spanish) as a real Spanish-speaking customer would. Follow Hispanic cultural and communication norms:
- Spanish-speaking customers value warmth and personal connection (personalismo): greetings and courtesy matter ("Buenos días", "Hola, ¿qué tal?"), and you may share some personal context about why the issue matters to you.
- Use "usted" with the agent by default; you may notice and react if the agent is overly cold, robotic, or rushes you.
- Express emotions openly and expressively when appropriate (frustration, gratitude), as is natural in Spanish conversation.
- Use natural expressions and fillers ("mire", "o sea", "la verdad", "¿me explico?") where they fit, without overdoing it.
- In emails, use a natural Spanish structure (e.g., "Estimados señores:", "Buenas tardes,") and polite, complete sentences.
- Use correct Spanish spelling and punctuation, including ¿ and ¡.`,
        assessmentGuidance: `Spanish is expected. Evaluate against professional Spanish-language customer service norms: appropriate use of "usted", warmth and personal rapport (personalismo), courteous greetings and closings ("¿Hay algo más en lo que le pueda ayudar?"), empathetic phrases ("Entiendo perfectamente su molestia", "Lamento mucho lo ocurrido"), and clear, well-structured explanations. A cold or overly transactional style should be treated as a tone issue. Recommended scripts must be natural, professional Spanish.`,
        emailClosing: 'Saludos cordiales,',
        names: {
            male: ['José', 'Luis', 'Carlos', 'Miguel', 'Alejandro', 'Javier', 'Diego', 'Fernando', 'Andrés', 'Ricardo'],
            female: ['María', 'Lucía', 'Sofía', 'Valentina', 'Camila', 'Isabel', 'Gabriela', 'Daniela', 'Carmen', 'Paula'],
            last: ['García', 'Rodríguez', 'Martínez', 'Hernández', 'López', 'González', 'Pérez', 'Sánchez', 'Ramírez', 'Torres', 'Flores', 'Rivera'],
        },
        ui: {
            overall: 'General',
            simulationResults: 'Resultados de la simulación',
            assessmentFrameworkApplied: 'Marco de evaluación aplicado',
            downloadReport: 'Descargar informe',
            newTraining: 'Nueva capacitación',
            scenario: 'Escenario',
            channel: 'Canal',
            duration: 'Duración',
            customer: 'Cliente',
            timelineAnalysis: 'Análisis de la línea de tiempo',
            focusedFor: (s) => `Enfoque de ~${s} s`,
        },
    },
    {
        value: 'Egyptian Arabic',
        label: 'Egyptian Arabic (العربية المصرية)',
        bcp47: 'ar-EG',
        dir: 'rtl',
        promptName: 'Egyptian Arabic (العامية المصرية)',
        customerInstruction: `You MUST converse ONLY in natural, conversational Egyptian Arabic (العامية المصرية / Masri) as a real customer in Egypt would. Do NOT use formal Modern Standard Arabic (فصحى) for conversation. Follow Egyptian cultural and communication norms:
- Use everyday Egyptian vocabulary and grammar (e.g., "إزيك", "عايز/عايزة", "دلوقتي", "ليه", "إيه", "مش", "بقالي", "لو سمحت", "معلش").
- Egyptians are warm, sociable and expressive; greetings and small courtesies matter ("السلام عليكم", "صباح الخير", "أهلاً"). Religious expressions are a natural part of speech ("إن شاء الله", "الحمد لله", "ربنا يخليك").
- Address the agent respectfully (e.g., "يا أستاذ", "يا فندم", "حضرتك"), and expect the agent to address you as "حضرتك" / "يا فندم".
- Express frustration openly and emotionally when appropriate, but realistically; you warm up quickly when treated with respect and genuine empathy.
- In emails, Egyptian customers may write slightly more formally, but still in a natural Egyptian style, and may open with "السلام عليكم" or "تحية طيبة وبعد".
- Write in Arabic script. Do NOT use Franco-Arabic (Arabizi / Latin letters with numbers).`,
        assessmentGuidance: `Egyptian Arabic (Masri) is expected. Evaluate against Egyptian customer service norms: respectful address ("حضرتك", "يا فندم"), warm greetings, genuine empathy and patience ("معلش", "أنا متفهم جداً"), courteous reassurance ("إن شاء الله هنحلها لحضرتك"), and clear explanations in natural Egyptian Arabic. Do not penalize the use of Egyptian dialect instead of Modern Standard Arabic; do not penalize common religious courtesy expressions. A cold, curt, or dismissive tone should be treated as a significant tone issue. Recommended scripts must be natural, professional Egyptian Arabic in Arabic script.`,
        emailClosing: 'مع خالص التحية،',
        names: {
            male: ['أحمد', 'محمد', 'محمود', 'مصطفى', 'عمر', 'كريم', 'يوسف', 'خالد', 'طارق', 'هشام'],
            female: ['فاطمة', 'منى', 'نور', 'سارة', 'ياسمين', 'مريم', 'هبة', 'دينا', 'رانيا', 'إيمان'],
            last: ['عبد الله', 'حسن', 'إبراهيم', 'السيد', 'مصطفى', 'عبد الرحمن', 'فؤاد', 'سالم', 'الشريف', 'منصور'],
        },
        ui: {
            overall: 'الإجمالي',
            simulationResults: 'نتائج المحاكاة',
            assessmentFrameworkApplied: 'إطار التقييم المطبق',
            downloadReport: 'تحميل التقرير',
            newTraining: 'تدريب جديد',
            scenario: 'السيناريو',
            channel: 'القناة',
            duration: 'المدة',
            customer: 'العميل',
            timelineAnalysis: 'تحليل التسلسل الزمني',
            focusedFor: (s) => `حوالي ${s} ثانية`,
        },
    },
    {
        value: 'German',
        label: 'German (Deutsch)',
        bcp47: 'de-DE',
        dir: 'ltr',
        promptName: 'German (Deutsch)',
        customerInstruction: `You MUST converse ONLY in natural, conversational German (Deutsch) as a real customer in Germany would. Follow German cultural and communication norms:
- Use "Sie" with the agent (formal address) and expect the agent to use "Sie" and your surname with "Herr"/"Frau". React with mild irritation if the agent uses "du" or is overly familiar.
- German communication is direct, factual and structured: state the problem clearly, give precise details (dates, numbers, reference numbers), and expect concrete answers, timelines and next steps rather than vague reassurances.
- Small talk is minimal; politeness is shown through correctness, punctuality and reliability, not through effusive friendliness.
- You may point out inconsistencies or broken promises plainly ("Das wurde mir aber anders zugesagt.").
- Use natural spoken German in voice/chat (e.g., "Also", "Genau", "Na ja", "Moment mal") where appropriate.
- In emails, follow German conventions (e.g., "Sehr geehrte Damen und Herren," ... ) with clear, well-organized paragraphs.`,
        assessmentGuidance: `German is expected. Evaluate against German customer service norms: consistent formal "Sie" address, correct use of "Herr/Frau + surname", precision and factual accuracy, clear structure, concrete next steps and binding timelines, and reliability of commitments. Empathy in German service is shown through competence and taking the issue seriously ("Ich verstehe Ihren Ärger, das ist natürlich ärgerlich.") rather than excessive apologies or flattery; vague or evasive answers should be treated as a clarity issue. Recommended scripts must be natural, professional German.`,
        emailClosing: 'Mit freundlichen Grüßen',
        names: {
            male: ['Lukas', 'Maximilian', 'Jonas', 'Felix', 'Tobias', 'Stefan', 'Andreas', 'Michael', 'Thomas', 'Jürgen'],
            female: ['Anna', 'Lena', 'Laura', 'Julia', 'Katharina', 'Sabine', 'Petra', 'Claudia', 'Monika', 'Sophie'],
            last: ['Müller', 'Schmidt', 'Schneider', 'Fischer', 'Weber', 'Meyer', 'Wagner', 'Becker', 'Schulz', 'Hoffmann', 'Koch', 'Richter'],
        },
        ui: {
            overall: 'Gesamt',
            simulationResults: 'Simulationsergebnisse',
            assessmentFrameworkApplied: 'Angewandter Bewertungsrahmen',
            downloadReport: 'Bericht herunterladen',
            newTraining: 'Neues Training',
            scenario: 'Szenario',
            channel: 'Kanal',
            duration: 'Dauer',
            customer: 'Kunde',
            timelineAnalysis: 'Zeitverlaufsanalyse',
            focusedFor: (s) => `Fokus ca. ${s} s`,
        },
    },
    {
        value: 'Italian',
        label: 'Italian (Italiano)',
        bcp47: 'it-IT',
        dir: 'ltr',
        promptName: 'Italian (Italiano)',
        customerInstruction: `You MUST converse ONLY in natural, conversational Italian (Italiano) as a real customer in Italy would. Follow Italian cultural and communication norms:
- Use the formal "Lei" with the agent and expect the agent to use "Lei" and appropriate titles ("Signor/Signora + surname").
- Italians are warm, expressive and relationship-oriented: courtesy and personal attention matter ("Buongiorno", "Senta, scusi…"), and you may explain the context and why the issue matters to you.
- Express emotions openly (frustration, disappointment, relief), using natural expressions ("Guardi", "Allora", "Insomma", "Mi scusi, ma…", "Ma come è possibile?").
- You appreciate an agent who listens, shows genuine understanding and takes personal ownership; you are put off by cold, bureaucratic or scripted answers.
- In emails, follow Italian conventions (e.g., "Gentili Signori," / "Buongiorno,") with polite, complete sentences.`,
        assessmentGuidance: `Italian is expected. Evaluate against Italian customer service norms: consistent formal "Lei" address, warm and courteous greetings, personal attention and ownership, genuine empathy ("Capisco perfettamente il disagio", "Mi dispiace molto per l'inconveniente"), patience with an expressive customer, and clear explanations. A cold, bureaucratic or overly scripted tone should be treated as a tone issue. Recommended scripts must be natural, professional Italian.`,
        emailClosing: 'Cordiali saluti,',
        names: {
            male: ['Marco', 'Luca', 'Giuseppe', 'Alessandro', 'Francesco', 'Matteo', 'Andrea', 'Giovanni', 'Stefano', 'Davide'],
            female: ['Giulia', 'Francesca', 'Chiara', 'Sara', 'Martina', 'Valentina', 'Elena', 'Alessia', 'Federica', 'Laura'],
            last: ['Rossi', 'Russo', 'Ferrari', 'Esposito', 'Bianchi', 'Romano', 'Colombo', 'Ricci', 'Marino', 'Greco', 'Bruno', 'Gallo'],
        },
        ui: {
            overall: 'Complessivo',
            simulationResults: 'Risultati della simulazione',
            assessmentFrameworkApplied: 'Quadro di valutazione applicato',
            downloadReport: 'Scarica report',
            newTraining: 'Nuova formazione',
            scenario: 'Scenario',
            channel: 'Canale',
            duration: 'Durata',
            customer: 'Cliente',
            timelineAnalysis: 'Analisi della cronologia',
            focusedFor: (s) => `Focus di ~${s} s`,
        },
    },
    {
        value: 'Chinese (Mandarin)',
        label: 'Chinese - Mandarin (简体中文)',
        bcp47: 'cmn-CN',
        dir: 'ltr',
        promptName: 'Mandarin Chinese (简体中文)',
        customerInstruction: `You MUST converse ONLY in natural, conversational Mandarin Chinese (普通话) as a real customer in mainland China would, written in Simplified Chinese characters (简体中文). Follow Chinese cultural and communication norms:
- Chinese communication is relatively high-context and "face" (面子) matters: you expect to be treated with respect, and you may become more insistent if you feel ignored, brushed off, or lose face.
- Use natural conversational markers ("你好", "麻烦你", "那个…", "就是说", "对啊", "好的", "怎么回事啊?").
- You expect the agent to use polite forms such as "您", "请", "非常抱歉", "麻烦您稍等一下", "感谢您的耐心".
- Efficiency and concrete results matter: you want to know exactly how and when the issue will be solved, and you may ask to escalate ("能不能找你们领导?") if the answer is vague.
- In emails, use a polite, structured Chinese format (e.g., "您好：" ... ) with clear paragraphs.
- Do NOT use pinyin or Traditional characters.`,
        assessmentGuidance: `Mandarin Chinese is expected. Evaluate against Chinese customer service norms: consistent use of the respectful "您", polite request forms ("请", "麻烦您"), sincere apologies ("非常抱歉给您带来不便"), protecting the customer's face (never blaming or contradicting the customer bluntly), patience, efficiency, and concrete commitments on resolution and timing. Vague answers or a lack of ownership should be treated as a clarity/resolution issue. Recommended scripts must be natural, professional Mandarin in Simplified Chinese.`,
        emailClosing: '此致敬礼',
        names: {
            male: ['伟', '强', '磊', '军', '洋', '勇', '杰', '涛', '明', '浩'],
            female: ['芳', '娜', '敏', '静', '丽', '艳', '婷', '雪', '慧', '琳'],
            last: ['王', '李', '张', '刘', '陈', '杨', '黄', '赵', '周', '吴', '徐', '孙'],
            familyNameFirst: true,
            separator: '',
        },
        ui: {
            overall: '总分',
            simulationResults: '模拟结果',
            assessmentFrameworkApplied: '已应用的评估框架',
            downloadReport: '下载报告',
            newTraining: '新的培训',
            scenario: '场景',
            channel: '渠道',
            duration: '时长',
            customer: '客户',
            timelineAnalysis: '时间线分析',
            focusedFor: (s) => `约${s}秒`,
        },
    },
    {
        value: 'Vietnamese',
        label: 'Vietnamese (Tiếng Việt)',
        bcp47: 'vi-VN',
        dir: 'ltr',
        promptName: 'Vietnamese (Tiếng Việt)',
        customerInstruction: `You MUST converse ONLY in natural, conversational Vietnamese (Tiếng Việt) as a real customer in Vietnam would, with full and correct diacritics. Follow Vietnamese cultural and communication norms:
- Vietnamese uses kinship-based pronouns that reflect age and respect. As the customer, refer to yourself and the agent naturally based on your persona's age (e.g., "tôi"/"em"/"chị"/"anh"/"cô"/"chú"), and expect the agent to address you respectfully as "anh/chị/quý khách" and refer to themselves as "em" or "tôi".
- Use natural politeness particles and markers ("ạ", "dạ", "vâng", "nhé", "à", "ơi", "cho tôi hỏi…").
- Vietnamese communication values harmony and indirectness; you may soften complaints at first, but become more direct if the issue isn't taken seriously.
- You notice and react if the agent omits polite particles like "dạ/ạ" or sounds abrupt.
- In emails, use a polite Vietnamese structure (e.g., "Kính gửi Quý công ty," ... ) with clear, respectful sentences.`,
        assessmentGuidance: `Vietnamese is expected. Evaluate against Vietnamese customer service norms: correct, respectful pronoun use ("anh/chị/quý khách" for the customer, "em" for the agent), consistent politeness particles ("dạ", "ạ", "vâng"), soft and harmonious phrasing, sincere apologies ("Em thành thật xin lỗi anh/chị vì sự bất tiện này"), patience, and clear guidance on next steps. Omitting politeness particles or using inappropriate pronouns should be treated as a tone issue. Recommended scripts must be natural, professional Vietnamese with correct diacritics.`,
        emailClosing: 'Trân trọng,',
        names: {
            male: ['Minh', 'Tuấn', 'Hùng', 'Dũng', 'Quang', 'Huy', 'Nam', 'Long', 'Khoa', 'Phúc'],
            female: ['Lan', 'Hương', 'Mai', 'Linh', 'Trang', 'Ngọc', 'Thảo', 'Hà', 'Phương', 'Anh'],
            last: ['Nguyễn', 'Trần', 'Lê', 'Phạm', 'Hoàng', 'Huỳnh', 'Phan', 'Vũ', 'Võ', 'Đặng', 'Bùi', 'Đỗ'],
            familyNameFirst: true,
            separator: ' ',
        },
        ui: {
            overall: 'Tổng điểm',
            simulationResults: 'Kết quả mô phỏng',
            assessmentFrameworkApplied: 'Khung đánh giá được áp dụng',
            downloadReport: 'Tải báo cáo',
            newTraining: 'Bài đào tạo mới',
            scenario: 'Tình huống',
            channel: 'Kênh',
            duration: 'Thời lượng',
            customer: 'Khách hàng',
            timelineAnalysis: 'Phân tích dòng thời gian',
            focusedFor: (s) => `Khoảng ${s} giây`,
        },
    },
];

/** Returns the config for a stored language value (falls back to a generic config for unknown values). */
export function getLanguageConfig(language: string | undefined): LanguageConfig {
    const found = LANGUAGES.find(l => l.value === language);
    if (found) return found;
    const name = language || 'English';
    return {
        value: name,
        label: name,
        bcp47: 'en-US',
        dir: 'ltr',
        promptName: name,
        customerInstruction: `Strictly use ${name} for all communications.`,
        assessmentGuidance: `Evaluate the language proficiency, tone, emotional intelligence, and soft skills according to the standard rules, customs, and cultural peculiarities of the ${name} language.`,
        emailClosing: 'Best regards,',
    };
}

/** Feedback screen labels for a language (English fallback). */
export function getFeedbackUiLabels(language: string | undefined): FeedbackUiLabels {
    return getLanguageConfig(language).ui || ENGLISH_UI;
}

/** True when AI-generated output should be in a language other than English. */
export function isNonEnglish(language: string | undefined): boolean {
    return !!language && language !== 'English';
}

/**
 * Generates a culturally appropriate full name for the language, or null if the
 * language has no dedicated name pool (caller should use the default generator).
 */
export function generateLocalizedName(language: string | undefined, gender: 'male' | 'female'): string | null {
    const cfg = getLanguageConfig(language);
    if (!cfg.names) return null;
    const pick = <T,>(arr: T[]) => arr[Math.floor(Math.random() * arr.length)];
    const first = pick(gender === 'male' ? cfg.names.male : cfg.names.female);
    const last = pick(cfg.names.last);
    const sep = cfg.names.separator ?? ' ';
    return cfg.names.familyNameFirst ? `${last}${sep}${first}` : `${first}${sep}${last}`;
}
